import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '../cn.js';
import { Button } from './Button.js';

/** P3 (Keka parity) — every open `SidePanel` used to register its OWN `document` keydown
 * listener, so one Escape press closed every stacked panel at once (a claim detail panel opened
 * from within a report, say). This module-level stack — mount order, not z-index, since every
 * panel shares the same fixed overlay anyway — lets Escape close only the most recently opened
 * one, the same "top of the stack first" behaviour a native dialog stack gives for free. */
let panelStack: symbol[] = [];

export interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Trailing header content next to the close button — a status pill, a reference number. */
  headerAction?: ReactNode;
  /** Sticky footer — usually the primary action row (Submit, Approve/Reject). */
  footer?: ReactNode;
  children?: ReactNode;
  /** Panel width in pixels. The New Claim form wants more room than the Claim Detail read view. */
  width?: number;
}

/**
 * A slide-over panel from the right — Stitch's "Panel A: Claim Details" / "Panel B: New Claim".
 *
 * NO RADIX DEPENDENCY, same rule as DropdownMenu.tsx: apps/web's local shadcn/Radix set is a known
 * divergence from this package's tokens, not the pattern to copy. Controlled rather than
 * uncontrolled — unlike DropdownMenu, the CONTENT depends on which record the caller selected
 * (which claim, or "new"), so that state has to live in the caller, not in this component.
 *
 * A backdrop rather than a full route change: these are Stitch panels over the list, not separate
 * pages — closing one returns to exactly the scroll position and filters the list had.
 */
/** Elements a keyboard user can actually land on — the same practical subset every hand-rolled
 * focus trap uses, since `:focusable` isn't a real CSS selector to query by. */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function SidePanel({
  open,
  onClose,
  title,
  subtitle,
  headerAction,
  footer,
  children,
  width = 440,
}: SidePanelProps) {
  // One per MOUNTED INSTANCE, not per open/close — a panel that closes and reopens (the common
  // "stays mounted, id just changes" convention several callers in this app already use) still
  // needs a stable identity for the stack below to track correctly across that transition.
  const idRef = useRef<symbol>(undefined);
  if (idRef.current === undefined) idRef.current = Symbol('side-panel');
  const asideRef = useRef<HTMLElement>(null);
  // The scrollable content area, so opening the panel can land focus on the first field the
  // caller actually rendered rather than on the header's own close button — see the effect below.
  const bodyRef = useRef<HTMLDivElement>(null);
  // A caller's `onClose` is almost always a fresh closure every render (every keystroke in the
  // form the panel holds, for instance) — depending on it directly below would tear down and
  // re-run the whole focus-management effect on every one of those renders, yanking focus out of
  // whatever field the person is actively typing in and back to the panel's first focusable
  // element. Found live: a form with no `useCallback` around its close handler was unusable to
  // type into. The ref keeps the effect's own dependency down to `open` alone, which is the only
  // thing that should ever re-run it, while `onKeyDown` below still always calls the latest close.
  // Synced in an effect rather than during render, so the ref is never written mid-render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const id = idRef.current!;
    panelStack.push(id);

    // P3 (Keka parity) — focus management, previously entirely absent: move focus INTO the panel
    // on open (so a screen-reader/keyboard user lands somewhere inside it, not wherever the page
    // happened to be), trap Tab/Shift+Tab within it while open (so tabbing never silently leaves
    // into the dimmed page behind the overlay), and give focus back to whatever had it before —
    // the element that opened this panel — once it closes.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusables = () =>
      Array.from(asideRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []);
    // WHERE focus lands is not simply "the first focusable in the panel": that is always the
    // header's own ✕ button, so every keyboard and screen-reader user was dropped on "Close"
    // instead of the thing they opened the panel to do, and it stole focus from callers that
    // place it themselves (TravelSidePanel's `useDialogFocus`, whose test caught this). Order:
    // an explicit `autofocus`, else the first focusable in the BODY, else the panel itself (the
    // fallback a native <dialog> uses when nothing inside it can take focus yet — content still
    // loading). The Tab trap below still cycles the FULL set, header and footer included.
    const explicit = asideRef.current?.querySelector<HTMLElement>('[autofocus]');
    const firstInBody = bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (explicit ?? firstInBody ?? asideRef.current)?.focus();

    function onKeyDown(event: KeyboardEvent) {
      // Only the topmost (most recently opened) panel reacts — a panel stacked underneath
      // another must wait its turn, not intercept Escape or Tab meant for the one on top.
      if (panelStack[panelStack.length - 1] !== id) return;
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Body scroll is locked while a panel is open — a background list scrolling under a fixed
    // overlay reads as broken, and it is the standard behaviour for this pattern everywhere else.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      panelStack = panelStack.filter((stackId) => stackId !== id);
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        type="button"
        aria-label="Close panel"
        onClick={onClose}
        className="absolute inset-0 bg-fg/40"
      />

      <aside
        ref={asideRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative flex h-dvh max-w-full flex-col border-l border-border bg-surface shadow-lg outline-none',
        )}
        style={{ width }}
      >
        <header className="flex items-start gap-3 border-b border-border p-4">
          <div className="min-w-0 flex-1">
            <h2 className="truncate font-display text-lg font-bold text-fg">{title}</h2>
            {subtitle && <p className="mt-0.5 truncate text-xs text-fg-muted">{subtitle}</p>}
          </div>
          {headerAction}
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </header>

        <div ref={bodyRef} className="flex-1 overflow-y-auto p-4">
          {children}
        </div>

        {footer && <footer className="border-t border-border p-4">{footer}</footer>}
      </aside>
    </div>
  );
}
